import { put, call, all, takeEvery, select } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import { APP, HOME, ENDPOINT } from "../constants";
import {
  nttKitaKantoTop10,
  nttKitaKantoTotalCount,
} from "../utils/mockdata/ntt";

import {
  setPageTopLoader,
  setHomeImageData,
  setHomeImageDataNtt,
  setHomeImageDataHokuden,
  setFilteredTotal,
  setHokudenOptionCity,
  setHokudenOptionBranch,
  setHomeMissionImageData,
  setSaitamaCsvExportLoading,
  setSaitamaCsvExportData,
  setSaitamaCsvExportError,
} from "../actions";

import { getJwtToken, getDataFromSxi, getDataFromKpiDash } from "../api";
import {
  PHOTO_REVIEW_STATUS,
  buildPhotoReviewStatusExistsClause,
  normalizePhotoReviewStatus,
} from "../utils/photoReviewFilter";
import {
  SAITAMA_CITY_SEASON_ID,
  isSaitamaSeasonSelected,
} from "../config/SaitamaCityConfig";
import {
  buildSaitamaPhotoGroupingCtes,
} from "../utils/helper/SaitamaCityPhotoQuery";
import { resolveSaitamaInspectionTables } from "../config/SaitamaInspectionResultTables";
import {
  SAITAMA_CSV_GAME_DB_BATCH_SIZE,
  chunkSaitamaCsvResultIds,
  correlateSaitamaCsvRows,
  dedupeSaitamaCsvRows,
  filterSaitamaCsvRowsByInspection,
  filterSaitamaCsvRowsBySelection,
} from "../utils/helper/saitamaCsvExport";

const getLocalStorageItem = (key) => {
  return localStorage.getItem(key);
};

const S3_BUCKET_BASE_URL = process.env.REACT_APP_S3_BUCKET_BASE_URL;

const keywords = ["ぬまづの宝", "防災", "観光", "聖地巡礼", "ピクトレ", "当別町", "苫前町", "体験ミッション", "にいがた"];

const buildFilteredPaeCte = ({ startUtc, endUtc }) => `
  WITH filtered_pae AS (
    SELECT
      power_asset_id,
      player_id,
      game_space_id,
      occurred_datetime
    FROM power_asset_event
    WHERE type = 'PHOTOGRAPH'
      AND occurred_datetime >= '${startUtc}'
      AND occurred_datetime < '${endUtc}'
  )
`;

const buildApprovedReviewRequestIdSubquery = ({
  photoAlias = "ppa",
  reviewReqIdInClause = "",
} = {}) => `
  (
    SELECT rrpb.review_request_id
    FROM review_image_info rii
    JOIN review_request_info_review_image_bind rririb
      ON rririb.review_image_info_id = rii.id
    JOIN review_request_review_request_info_bind rrpb
      ON rrpb.review_request_info_id = rririb.review_request_info_id
    JOIN review_request_event rre
      ON rre.review_request_id = rrpb.review_request_id
    WHERE rii.photo_of_power_asset_id = ${photoAlias}.id
      AND rre.type = 'APPROVED'
      ${reviewReqIdInClause}
    LIMIT 1
  )
`;

export const buildSaitamaPhotographyOrder = (order) => order === "asc" ? "ASC" : "DESC";

export const buildSaitamaManagementWhere = (managementId) => {
  const value = String(managementId || "").trim();
  return value
    ? `AND padsc.management_id = '${value.replace(/'/g, "''")}'`
    : "";
};

const buildApprovedReviewExistsClause = ({
  photoAlias = "ppa",
  reviewReqIdInClause = "",
} = {}) => `
  EXISTS (
    SELECT 1
    FROM review_image_info rii
    JOIN review_request_info_review_image_bind rririb
      ON rririb.review_image_info_id = rii.id
    JOIN review_request_info rri
      ON rri.id = rririb.review_request_info_id
    JOIN review_request_review_request_info_bind rrpb
      ON rrpb.review_request_info_id = rri.id
    JOIN review_request_event rre
      ON rre.review_request_id = rrpb.review_request_id
      AND rre.type = 'APPROVED'
    WHERE rii.photo_of_power_asset_id = ${photoAlias}.id
    ${reviewReqIdInClause}
  )
`;

const buildDetailTableRef = ({
  tableName,
  alias = "padtep",
  baseAlias = "pae",
} = {}) => `${tableName} ${alias} ON ${baseAlias}.power_asset_id = ${alias}.power_asset_id`;

const buildPadTypeWhereClause = ({
  alias = "pad",
  typeName = "",
} = {}) => (typeName ? `AND ${alias}.type = '${typeName}'` : "");

const isMissionKeywordSelected = (missionSelectedName = "") =>
  keywords.some((keyword) => missionSelectedName.includes(keyword));

const buildCompanyDetailContext = ({
  provider,
  type,
  missionSelectedName = "",
  isMe = false,
  gameSpaceIdSelected = "",
} = {}) => {
  if (provider === "hepco") {
    const detailTableName = "power_asset_detail_of_hepco";
    return {
      detailTableName,
      detailTableRef: buildDetailTableRef({
        tableName: detailTableName,
      }),
      padTypeWhereClause: buildPadTypeWhereClause({
        typeName: "HEPCO",
      }),
    };
  }

  if (provider === "tepco") {
    const useMissionVenue =
      type === "mission" && isMissionKeywordSelected(missionSelectedName);
    const detailTableName = useMissionVenue
      ? "power_asset_detail_of_mission_venue"
      : "power_asset_detail_of_tepco";

    return {
      detailTableName,
      detailTableRef: buildDetailTableRef({
        tableName: detailTableName,
      }),
      padTypeWhereClause:
        type === "mission" && !useMissionVenue
          ? buildPadTypeWhereClause({
              typeName: "TEPCO",
            })
          : "",
    };
  }

  if (provider === "ntt") {
    const detailTableName =
      type === "mission"
        ? "power_asset_detail_of_mission_venue"
        : isMe
        ? "power_asset_detail_of_ntt_me"
        : "power_asset_detail_of_ntt_ft";

    return {
      detailTableName,
      detailTableRef: buildDetailTableRef({
        tableName: detailTableName,
      }),
      padTypeWhereClause:
        gameSpaceIdSelected === "cmb68qvei00011vo0e591229z"
          ? buildPadTypeWhereClause({
              typeName: "NTT",
            })
          : "",
    };
  }

  return {
    detailTableName: "",
    detailTableRef: "",
    padTypeWhereClause: "",
  };
};

export const periodType = (state) => state.app.periodType;
export const selectedSeason = (state) => state.app.selectedSeason;
export const selectedMission = (state) => state.app.selectedMission;
export const selectedMissionLabel = (state) => state.app.selectedMissionLabel;
export const startDate = (state) => state.app.dateRange[0];
export const endDate = (state) => state.app.dateRange[1];
export const filter = (state) => state.home.filter;
export const filterNtt = (state) => state.home.filterNtt;
export const filterHokuden = (state) => state.home.filterHokuden;
export const saitamaCsvSelection = (state) => state.home.saitamaCsvSelection;

export const isInvalidSaitamaCsvRowSelection = (
  hasSelectedRows,
  selection = [],
  selectedCanonicalCount = 0
) => hasSelectedRows && (
  selection.length === 0 ||
  selection.length !== Number(selectedCanonicalCount)
);

const normalizeSaitamaCsvResponseRows = (response, columns = []) => {
  const body = response?.body ?? response;
  const parsed = typeof body === "string" ? JSON.parse(body || "[]") : body;
  if (!Array.isArray(parsed)) {
    throw new Error("Saitama CSV API response is not an array");
  }
  return parsed.map((row) => {
    if (!Array.isArray(row) || columns.length === 0) return row;
    return columns.reduce((result, column, index) => {
      const cell = row[index];
      result[column] = cell == null
        ? null
        : cell.stringValue ?? cell.longValue ?? cell.doubleValue ?? cell.booleanValue ?? cell;
      return result;
    }, {});
  });
};

const buildSaitamaCsvIdentityPredicate = (selection, escapeSql) => {
  const predicates = (selection || [])
    .filter((identity) => identity?.game_space_id && identity?.photograph_event_id)
    .map((identity) => `(
      rpg.game_space_id = '${escapeSql(identity.game_space_id)}'
      AND rpg.photograph_event_id = '${escapeSql(identity.photograph_event_id)}'
    )`);
  if (!Array.isArray(selection) || selection.length === 0) return "";
  return predicates.length > 0 ? `AND (${predicates.join(" OR ")})` : "AND 1 = 0";
};

export const buildSaitamaCsvPopulationQueries = ({
  photoGroupCte,
  managementWhere = "",
  firstCaptureWhere = "",
  selectedIdentities = [],
  orderValue = "DESC",
  limit = SAITAMA_CSV_GAME_DB_BATCH_SIZE,
  offset = 0,
} = {}) => {
  const escapeSql = (value) => String(value ?? "").replace(/'/g, "''");
  const selectionWhere = buildSaitamaCsvIdentityPredicate(selectedIdentities, escapeSql);
  const fromAndWhere = `
    FROM SaitamaRankedPhotoGroups rpg
    JOIN power_asset pa ON pa.id = rpg.power_asset_id
    LEFT JOIN power_asset_detail_of_saitama_city padsc
      ON padsc.power_asset_id = rpg.power_asset_id
    LEFT JOIN (
      SELECT player_id, MAX(name) AS name
      FROM player_state
      GROUP BY player_id
    ) ps ON ps.player_id = rpg.player_id
    WHERE rpg.group_rn = 1
      ${managementWhere}
      ${firstCaptureWhere}
      ${selectionWhere}
  `;
  const columns = `
    rpg.ppa_id,
    rpg.file_name,
    rpg.object_key,
    rpg.power_asset_id,
    rpg.player_id,
    rpg.game_space_id,
    rpg.logical_group_key,
    rpg.photograph_event_id,
    padsc.management_id AS saitama_management_id,
    ps.name,
    rpg.occurred_datetime,
    CONVERT_TZ(rpg.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
    pa.latitude,
    pa.longitude
  `;
  return {
    countQuery: {
      query: `WITH ${photoGroupCte} SELECT COUNT(*) AS total_count ${fromAndWhere};`,
      type: "select",
    },
    populationQuery: {
      query: `WITH ${photoGroupCte} SELECT ${columns} ${fromAndWhere}
        ORDER BY rpg.occurred_datetime ${orderValue}, rpg.photograph_event_id ${orderValue}
        LIMIT ${Number(limit)} OFFSET ${Number(offset)};`,
      type: "select",
    },
  };
};

export function* handleSaitamaCsvExport(action = {}) {
  yield put(setSaitamaCsvExportLoading(true));
  try {
    const { resultTable, itemTable } = resolveSaitamaInspectionTables();
    const dateFrom = yield select(startDate);
    const dateTo = yield select(endDate);
    const assetFilter = yield select(filter);
    const selection = yield select(saitamaCsvSelection);
    const hasSelectedRows = Boolean(action.payload?.hasSelectedRows);
    const selectedCanonicalCount = Number(action.payload?.selectedCanonicalCount || 0);
    if (isInvalidSaitamaCsvRowSelection(
      hasSelectedRows,
      selection,
      selectedCanonicalCount
    )) {
      yield put(setSaitamaCsvExportError(
        "CSV出力にはphotograph_event_idを持つ写真行のみ選択してください"
      ));
      return;
    }
    const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const photoGroupCte = buildSaitamaPhotoGroupingCtes({
      startUtc: utcFrom,
      endUtc: utcToExclusive,
    });
    const managementWhere = buildSaitamaManagementWhere(assetFilter.managementId);
    const firstCaptureWhere = assetFilter.firstCaptureOnly
      ? "AND rpg.first_capture_rn = 1"
      : "";
    const orderValue = buildSaitamaPhotographyOrder(assetFilter.order);
    const exportRows = [];
    let offset = 0;
    let batchRows;
    do {
      const queries = buildSaitamaCsvPopulationQueries({
        photoGroupCte,
        managementWhere,
        firstCaptureWhere,
        selectedIdentities: selection,
        orderValue,
        limit: SAITAMA_CSV_GAME_DB_BATCH_SIZE,
        offset,
      });
      batchRows = normalizeSaitamaCsvResponseRows(
        yield call(getDataFromKpiDash, queries.populationQuery, ENDPOINT.GET_DATA_API_REPLICA),
        [
          "ppa_id", "file_name", "object_key", "power_asset_id", "player_id",
          "game_space_id", "logical_group_key", "photograph_event_id",
          "saitama_management_id", "name", "occurred_datetime",
          "occurred_datetime_jst", "latitude", "longitude",
        ]
      );
      const eventIds = [...new Set(batchRows.map((row) => row.photograph_event_id).filter(Boolean))];
      let inspectionRows = [];
      for (const eventChunk of chunkSaitamaCsvResultIds(eventIds)) {
        const eventIdSql = eventChunk.map((id) => `'${String(id).replace(/'/g, "''")}'`).join(", ");
        const response = yield call(
          getDataFromKpiDash,
          {
            query: `
              SELECT r.id, r.game_space_id, r.photograph_event_id,
                     i.inspection_item_code
              FROM ${resultTable} r
              LEFT JOIN ${itemTable} i ON i.result_id = r.id
              WHERE r.game_space_id = '${SAITAMA_CITY_SEASON_ID}'
                AND r.photograph_event_id IN (${eventIdSql});
            `,
            type: "select",
          },
          ENDPOINT.GET_DATA_KPIDASH_API
        );
        inspectionRows = inspectionRows.concat(normalizeSaitamaCsvResponseRows(
          response,
          ["id", "game_space_id", "photograph_event_id", "inspection_item_code"]
        ));
      }
      let batchExportRows = correlateSaitamaCsvRows(batchRows, inspectionRows);
      batchExportRows = filterSaitamaCsvRowsBySelection(batchExportRows, selection);
      batchExportRows = filterSaitamaCsvRowsByInspection(batchExportRows, assetFilter.inspectionItems);
      exportRows.push(...batchExportRows);
      offset += SAITAMA_CSV_GAME_DB_BATCH_SIZE;
    } while (batchRows.length > 0);

    const normalizedExportRows = dedupeSaitamaCsvRows(exportRows);
    if (normalizedExportRows.length === 0) {
      yield put(setSaitamaCsvExportError("CSV出力対象のデータがありません"));
      return;
    }
    yield put(setSaitamaCsvExportData(normalizedExportRows));
  } catch (error) {
    console.log(error);
    yield put(setSaitamaCsvExportError(error.message || "CSV出力対象の取得に失敗しました"));
  }
}

export function* handleHomeImageDataSaitama(action) {
  try {
    const requestSeasonId = yield select(selectedSeason);
    if (!isSaitamaSeasonSelected(localStorage.getItem("access_key"), requestSeasonId)) {
      return;
    }
    const { resultTable, itemTable } = resolveSaitamaInspectionTables();
    yield put(setPageTopLoader(true));
    yield put(setFilteredTotal("--"));

    const dateFrom = yield select(startDate);
    const dateTo = yield select(endDate);
    const pageNumber = action.payload?.pageNumber || 1;
    const itemsPerPage = 10;
    const offset = (pageNumber - 1) * itemsPerPage;
    const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const photoGroupCte = buildSaitamaPhotoGroupingCtes({
      startUtc: utcFrom,
      endUtc: utcToExclusive,
    });
    const assetFilter = yield select(filter);
    const escapeSql = (value) => String(value ?? "").replace(/'/g, "''");
    const managementId = String(assetFilter.managementId || "").trim();
    const inspectionItems = Array.isArray(assetFilter.inspectionItems)
      ? assetFilter.inspectionItems.filter(Boolean)
      : [];
    const inspectionCodes = inspectionItems
      .filter((code) => code !== "UNINSPECTED")
      .map((code) => `'${escapeSql(code)}'`)
      .join(", ");
    const hasUninspected = inspectionItems.includes("UNINSPECTED");
    const managementWhere = buildSaitamaManagementWhere(managementId);
    const firstCaptureWhere = assetFilter.firstCaptureOnly
      ? "AND rpg.first_capture_rn = 1"
      : "";
    const orderValue = buildSaitamaPhotographyOrder(assetFilter.order);

    const parseRows = (response, label, columns = []) => {
      const body = response?.body ?? response;
      const parsed = typeof body === "string" ? JSON.parse(body || "[]") : body;
      if (!Array.isArray(parsed)) {
        throw new Error(`${label} response is not an array`);
      }
      return parsed.map((row) => {
        if (!Array.isArray(row) || columns.length === 0) return row;
        return columns.reduce((result, column, index) => {
          const cell = row[index];
          result[column] = cell == null
            ? null
            : cell.stringValue ?? cell.longValue ?? cell.doubleValue ?? cell.booleanValue ?? cell;
          return result;
        }, {});
      });
    };

    // The Photography query runs through /api/data-replica (game DB), while
    // Saitama inspection results live in KpiDashboard behind /api/data-kpidash.
    // Resolve result-event IDs separately instead of cross-database JOINs.
    let inspectionFilterEventIds = null;
    let allResultEventIds = [];
    if (inspectionCodes || hasUninspected) {
      const resultResponse = yield call(
        getDataFromKpiDash,
        {
          query: `
            SELECT r.photograph_event_id, i.inspection_item_code
            FROM ${resultTable} r
            LEFT JOIN ${itemTable} i
              ON i.result_id = r.id
            WHERE r.game_space_id = '${SAITAMA_CITY_SEASON_ID}';
          `,
          type: "select",
        },
        ENDPOINT.GET_DATA_KPIDASH_API
      );
      const resultRows = parseRows(
        resultResponse,
        "Saitama inspection filter",
        ["photograph_event_id", "inspection_item_code"]
      );
      const resultByEvent = new Map();
      resultRows.forEach((row) => {
        const eventId = row.photograph_event_id;
        if (!eventId) return;
        if (!resultByEvent.has(eventId)) resultByEvent.set(eventId, new Set());
        if (row.inspection_item_code) resultByEvent.get(eventId).add(row.inspection_item_code);
      });
      allResultEventIds = [...resultByEvent.keys()];
      const requestedCodes = inspectionItems.filter((code) => code !== "UNINSPECTED");
      const matchingEventIds = [...resultByEvent.entries()]
        .filter(([, codes]) => requestedCodes.some((code) => codes.has(code)))
        .map(([eventId]) => eventId);
      const sqlIds = (ids) => ids.map((id) => `'${escapeSql(id)}'`).join(", ");
      const matchingPredicate = matchingEventIds.length > 0
        ? `rpg.photograph_event_id IN (${sqlIds(matchingEventIds)})`
        : "";
      const uninspectedPredicate = hasUninspected
        ? allResultEventIds.length > 0
          ? `(rpg.photograph_event_id IS NULL OR rpg.photograph_event_id NOT IN (${sqlIds(allResultEventIds)}))`
          : "1 = 1"
        : "";
      inspectionFilterEventIds = [matchingPredicate, uninspectedPredicate].filter(Boolean).join(" OR ");
      if (!inspectionFilterEventIds && requestedCodes.length > 0) {
        inspectionFilterEventIds = "1 = 0";
      }
    }
    const inspectionWhereForGameDb = inspectionFilterEventIds
      ? `AND (${inspectionFilterEventIds})`
      : "";

    const fromAndWhere = `
      FROM SaitamaRankedPhotoGroups rpg
      JOIN power_asset pa ON pa.id = rpg.power_asset_id
      LEFT JOIN power_asset_detail_of_saitama_city padsc
        ON padsc.power_asset_id = rpg.power_asset_id
      LEFT JOIN (
        SELECT player_id, MAX(name) AS name
        FROM player_state
        GROUP BY player_id
      ) ps ON ps.player_id = rpg.player_id
      WHERE rpg.group_rn = 1
        ${managementWhere}
        ${inspectionWhereForGameDb}
        ${firstCaptureWhere}
    `;
    const listQuery = {
      query: `
        WITH ${photoGroupCte}
        SELECT
          rpg.ppa_id,
          rpg.file_name,
          rpg.object_key,
          rpg.power_asset_id,
          rpg.player_id,
          rpg.game_space_id,
          rpg.logical_group_key,
          rpg.photograph_event_id,
          padsc.management_id AS saitama_management_id,
          ps.name,
          rpg.occurred_datetime,
          CONVERT_TZ(rpg.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
          pa.latitude,
          pa.longitude
        ${fromAndWhere}
        ORDER BY rpg.occurred_datetime ${orderValue}, rpg.photograph_event_id ${orderValue}
        LIMIT ${itemsPerPage} OFFSET ${offset};
      `,
    };
    const countQuery = {
      query: `
        WITH ${photoGroupCte}
        SELECT COUNT(*) AS total_count
        ${fromAndWhere};
      `,
    };
    const [listResponse, countResponse] = yield all([
      call(getDataFromKpiDash, listQuery, ENDPOINT.GET_DATA_API_REPLICA),
      call(getDataFromKpiDash, countQuery, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    const rows = parseRows(listResponse, "Saitama Photography list");
    const countRows = parseRows(countResponse, "Saitama Photography count");
    const eventIds = rows.map((row) => row.photograph_event_id).filter(Boolean);
    const resultByEvent = new Map();
    if (eventIds.length > 0) {
      const eventIdSql = eventIds.map((id) => `'${escapeSql(id)}'`).join(", ");
      const resultResponse = yield call(
        getDataFromKpiDash,
        {
          query: `
            SELECT r.id, r.photograph_event_id, i.inspection_item_code
            FROM ${resultTable} r
            LEFT JOIN ${itemTable} i
              ON i.result_id = r.id
            WHERE r.game_space_id = '${SAITAMA_CITY_SEASON_ID}'
              AND r.photograph_event_id IN (${eventIdSql});
          `,
          type: "select",
        },
        ENDPOINT.GET_DATA_KPIDASH_API
      );
      parseRows(
        resultResponse,
        "Saitama inspection list enrichment",
        ["id", "photograph_event_id", "inspection_item_code"]
      ).forEach((row) => {
        if (!resultByEvent.has(row.photograph_event_id)) {
          resultByEvent.set(row.photograph_event_id, {
            id: row.id,
            items: [],
          });
        }
        if (row.inspection_item_code) {
          resultByEvent.get(row.photograph_event_id).items.push(row.inspection_item_code);
        }
      });
    }
    const photoAllData = rows.map((photoData) => ({
      signNumber: "--",
      signName: "--",
      name: "--",
      placeCode: "--",
      tag: [],
      user: photoData.name || photoData.player_id,
      fileUrl: `${S3_BUCKET_BASE_URL}/${encodeURIComponent(photoData.object_key)}`,
      fileName: photoData.file_name.replace(/\.(jpg|webp)$/i, ""),
      groupKey: photoData.logical_group_key,
      gameSpaceId: photoData.game_space_id,
      photographEventId: photoData.photograph_event_id,
      occurred_datetime: photoData.occurred_datetime_jst || null,
      latitude: photoData.latitude,
      longitude: photoData.longitude,
      saitamaManagementId: photoData.saitama_management_id,
      inspectionResultId: resultByEvent.get(photoData.photograph_event_id)?.id,
      inspectionItems: resultByEvent.get(photoData.photograph_event_id)?.items || [],
    }));
    if (requestSeasonId !== (yield select(selectedSeason))) {
      yield put(setPageTopLoader(false));
      return;
    }
    yield put(setFilteredTotal(countRows[0]?.total_count ?? 0));
    yield put(setHomeImageData(photoAllData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
    yield put(setPageTopLoader(false));
  }
}

export function* handleHomeImageDataTepco(action) {
  try {
    const requestSeasonId = yield select(selectedSeason);
    if (isSaitamaSeasonSelected(localStorage.getItem("access_key"), requestSeasonId)) {
      yield* handleHomeImageDataSaitama(action);
      return;
    }
    const isNtt =
      localStorage.getItem("access_key") === "qf70ypdjdzm89ypqvc4r7nf9";
    const jwtToken = yield call(getJwtToken);
    yield put(setPageTopLoader(true));
    yield put(setFilteredTotal("--"));
    const env = process.env.REACT_APP_ENV;
    const type = yield select(periodType);
    let dateFrom = yield select(startDate);
    let dateTo = yield select(endDate);

    const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // 半開区間：dateTo の翌日 00:00 JST を UTC にして「未満」で切る
    const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const assetFilter = yield select(filter);
    const reviewStatus =
      normalizePhotoReviewStatus(assetFilter.reviewStatus) ||
      PHOTO_REVIEW_STATUS.APPROVED;
    const photoReviewStatusQuery = buildPhotoReviewStatusExistsClause({
      photoAlias: "ppa",
      status: reviewStatus,
    });
    const seasonIdSelected = yield select(selectedSeason);
    const missioIdSelected = yield select(selectedMission);
    const missionSelectedName = yield select(selectedMissionLabel);
    dateFrom = moment(dateFrom).tz("Asia/Tokyo").format("YYYY-MM-DD");
    dateTo = moment(dateTo).tz("Asia/Tokyo").format("YYYY-MM-DD");
    const pageNumber =
      action.payload.pageNumber === undefined
        ? 1
        : action.payload.pageNumber || 1;
    const itemsPerPage = 10;
    const offset = (pageNumber - 1) * itemsPerPage;
    const orderValue = assetFilter.order == "desc" ? "DESC" : "ASC";
    const equipmentNumber =
      assetFilter.assetId === null ? false : assetFilter.assetId;
    const signNumber =
      assetFilter.signNumber === null ? false : assetFilter.signNumber;
    let assetType =
      assetFilter.assetType === "all" ? false : assetFilter.assetType;
    const manageGroup =
      assetFilter.manageGroup.key === "all"
        ? false
        : assetFilter.manageGroup.value;

    // 地上設備 x 2種類
    let assetType2 = "";
    if (assetType && assetType.includes("GROUND_EQUIPMENT")) {
      assetType2 = assetType.split("-")[1];
      assetType = assetType.split("-")[0];
    }

    const seasonIdQuery =
      type === "season" && seasonIdSelected !== "all"
        ? `AND ppa.game_space_id = '${seasonIdSelected}'`
        : "";

    const missionIdQUery =
      type === "mission" &&
      missioIdSelected !== "blank" &&
      missioIdSelected !== "all"
        ? `AND pae.game_space_id = '${missioIdSelected}'`
        : "";

    const assetIdQuery = equipmentNumber
      ? `AND padtep.equipment_number = '${equipmentNumber}'`
      : "";

    const signNumberQuery = signNumber
      ? `AND padtep.sign_number = '${signNumber}'`
      : "";

    let assetTypeQuery =
      assetType && assetType !== "POWER_POLE_HAS_PROTECTIVE_TUBE_ALL"
        ? `AND pa.type = '${assetType}'`
        : "";

    const assetTypeQuery2 =
      assetType2 !== "" ? `AND padtep.equipment_code = '${assetType2}'` : "";

    // 防護管付き電柱・全て & 写真あり
    let bogokanJoinQuery = ``;
    let bogokanWhereQuery = ``;
    if (assetType && assetType.includes("POWER_POLE_HAS_PROTECTIVE_TUBE_ALL")) {
      bogokanJoinQuery = `JOIN power_pole pp ON ppa.power_asset_id = pp.id`;
      bogokanWhereQuery = `AND pp.power_pole_type = 'HAS_PROTECTIVE_TUBE'`;
    } else if (
      assetType &&
      assetType.includes("POWER_POLE_HAS_PROTECTIVE_TUBE_WITH_PIC")
    ) {
      bogokanJoinQuery = `
        JOIN power_asset_angle_of_view_name_v2 paaovn ON paaovn.power_asset_photography_target_id = ppa.power_asset_photography_target_id
        AND paaovn.language_code = 'JA'
        JOIN power_pole pp ON ppa.power_asset_id = pp.id
      `;
      bogokanWhereQuery = `AND pp.power_pole_type = 'HAS_PROTECTIVE_TUBE' AND paaovn.angle_of_view_name like '% - 防護管%'`;
      assetTypeQuery = "";
    }

    // 高圧設備あり
    let plantInsulateSwitchJoinQuery = ``;
    let plantInsulateSwitchWhereQuery = ``;
    if (assetType && assetType.includes("POWER_POLE_HAS_INSULATE_SWITCH")) {
      plantInsulateSwitchJoinQuery = `LEFT OUTER JOIN power_asset_id_with_air_insulated_switch paiwais ON paiwais.power_asset_id = pa.id`;
      plantInsulateSwitchWhereQuery = `
      AND pa.type = 'POWER_POLE'
      AND paiwais.power_asset_id is not NULL`;
      assetTypeQuery = "";
    }

    // 低圧
    if (assetType && assetType.includes("POWER_POLE_NO_INSULATE_SWITCH")) {
      plantInsulateSwitchJoinQuery = `LEFT OUTER JOIN power_asset_id_with_air_insulated_switch paiwais ON paiwais.power_asset_id = pa.id`;
      plantInsulateSwitchWhereQuery = `
      AND pa.type = 'POWER_POLE'
      AND paiwais.power_asset_id is NULL`;
      assetTypeQuery = "";
    }

    // ツルツタ台帳記載・高圧設備なし
    if (assetType && assetType.includes("POWER_POLE_HAS_PLNAT")) {
      plantInsulateSwitchJoinQuery = `LEFT OUTER JOIN power_asset_id_with_air_insulated_switch paiwais ON paiwais.power_asset_id = pa.id`;
      plantInsulateSwitchWhereQuery = `
        AND pa.type = 'POWER_POLE'
        AND pa.sub_type = 'WITH_PLANTS'
      `;
      assetTypeQuery = "";
    }

    // ツルツタ台帳記載・高圧設備あり
    if (
      assetType &&
      assetType.includes("POWER_POLE_HAS_PLNAT_AND_INSULATE_SWITCH")
    ) {
      plantInsulateSwitchJoinQuery = `LEFT OUTER JOIN power_asset_id_with_air_insulated_switch paiwais ON paiwais.power_asset_id = pa.id`;
      plantInsulateSwitchWhereQuery = `
        AND pa.type = 'POWER_POLE'
        AND pa.sub_type = 'WITH_PLANTS'
        AND paiwais.power_asset_id is not NULL
      `;
      assetTypeQuery = "";
    }

    // 配電保守グループ
    let managementGroupQuery = ``;
    if (manageGroup) {
      const manageGroupCommaSeparated = manageGroup.join("','");
      managementGroupQuery = `AND padtep.place_code IN ('${manageGroupCommaSeparated}')`;
    }

    const dateRangeQuery =
      (type === "season" || type === "mission")
        ? `AND pae.occurred_datetime >= '${utcFrom}' AND pae.occurred_datetime <  '${utcToExclusive}'`
        : "";

    const tepcoDetailContext = buildCompanyDetailContext({
      provider: "tepco",
      type,
      missionSelectedName,
    });
    const assetDetailQuery = tepcoDetailContext.detailTableRef;
    const reviewRequestState = type === "mission" ? `` : `AND rre.type = 'APPROVED'`;

    // TEPCO外のミッション対応
    const companyQuery = tepcoDetailContext.padTypeWhereClause;

    const tepcoNewQuerySeasonIds = [
      "cmltfi5lz0001b7pl9874gkwd",
      "cmltfke6u0001pcplb6xja27x",
    ];
    let isNewQuery = true; //tepcoNewQuerySeasonIds.includes(seasonIdSelected);
    let sqlQuery = {};
    sqlQuery.query = `
      SELECT
          ppa.*,
          gs.*,
          gt.*,
          pae.occurred_datetime AS occurred_datetime,
          CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
          ps.*,
          pad.*,
          padtep.*,
          pa.latitude,
          pa.longitude,
          rrpb.review_request_id
      FROM
          power_asset_event pae
      JOIN
          photo_of_power_asset ppa ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id = pae.player_id
          AND ppa.game_space_id = pae.game_space_id
      JOIN
          power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
      JOIN
          game_space gs ON gs.id = pae.game_space_id
      JOIN
          game_term gt ON gt.id = gs.game_term_id
      JOIN
          player_state ps ON pae.player_id = ps.player_id
      JOIN
          ${assetDetailQuery}
      JOIN
            review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
        JOIN
            review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
        JOIN
            review_request_info rri ON rri.id = rririb.review_request_info_id
        JOIN
            review_request_review_request_info_bind rrpb ON rrpb.review_request_info_id = rri.id
      JOIN
        	power_asset pa ON pa.id = pae.power_asset_id
      INNER JOIN review_request_event rre ON rrpb.review_request_id = rre.review_request_id
      ${bogokanJoinQuery}
      ${plantInsulateSwitchJoinQuery}
      WHERE
          pae.type = 'PHOTOGRAPH'
          ${reviewRequestState}
          ${companyQuery}
          ${dateRangeQuery}
          ${seasonIdQuery}
          ${missionIdQUery}
          ${assetIdQuery}
          ${signNumberQuery}
          ${assetTypeQuery}
          ${assetTypeQuery2}
          ${bogokanWhereQuery}
          ${managementGroupQuery}
          ${plantInsulateSwitchWhereQuery}
      GROUP BY
          pae.power_asset_id, ps.player_id, occurred_datetime
      ORDER BY
          pae.occurred_datetime ${orderValue}
      LIMIT ${itemsPerPage}
      OFFSET ${offset};
    `;

    if (isNewQuery) {
      sqlQuery.query = `
        WITH ReviewFilteredPhotos AS (
          SELECT DISTINCT
            ppa.id AS ppa_id,
            ppa.power_asset_id,
            rr.id AS review_request_id
          FROM power_asset_event pae
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id = pae.player_id
            AND ppa.game_space_id = pae.game_space_id
          LEFT JOIN review_image_info rii
            ON rii.photo_of_power_asset_id = ppa.id
          LEFT JOIN review_request_info_review_image_bind rririb
            ON rririb.review_image_info_id = rii.id
          LEFT JOIN review_request_review_request_info_bind rrpb
            ON rrpb.review_request_info_id = rririb.review_request_info_id
          LEFT JOIN review_request rr
            ON rr.id = rrpb.review_request_id
          WHERE pae.type = 'PHOTOGRAPH'
            AND pae.game_space_id = '${type === "season" ? seasonIdSelected : missioIdSelected}'
            ${dateRangeQuery}
            ${seasonIdQuery}
            ${missionIdQUery}
            AND ${photoReviewStatusQuery}
        ),
        RankedPhotos AS (
          SELECT
            ppa.id AS ppa_id,
            ppa.player_id,
            ppa.file_name,
            ppa.object_key,
            gs.id AS game_space_id,
            gt.id AS game_term_id,
            pae.occurred_datetime,
            CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.name,
            rfp.review_request_id,
            padtep.*,
            pa.latitude,
            pa.longitude,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime ${orderValue}
            ) AS rn
          FROM ReviewFilteredPhotos rfp
          JOIN power_asset_event pae
            ON pae.power_asset_id = rfp.power_asset_id
          AND pae.type = 'PHOTOGRAPH'
          AND pae.game_space_id = '${type === "season" ? seasonIdSelected : missioIdSelected}'
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id      = pae.player_id
          AND ppa.game_space_id  = pae.game_space_id
          JOIN game_space gs ON gs.id = pae.game_space_id
          JOIN game_term gt  ON gt.id = gs.game_term_id
          JOIN player_state ps ON pae.player_id = ps.player_id
          JOIN ${assetDetailQuery}
          JOIN power_asset pa ON pa.id = pae.power_asset_id
          WHERE 1=1
            ${companyQuery}
            ${dateRangeQuery}
            ${seasonIdQuery}
            ${missionIdQUery}
            ${assetIdQuery}
            ${signNumberQuery}
            ${assetTypeQuery}
            ${assetTypeQuery2}
            ${bogokanWhereQuery}
            ${managementGroupQuery}
            ${plantInsulateSwitchWhereQuery}
        )
        SELECT *
        FROM RankedPhotos
        WHERE rn = 1
        ORDER BY occurred_datetime ${orderValue}
        LIMIT ${itemsPerPage} OFFSET ${offset};
      `;
    }

    // COUNT
    let sqlQueryCount = {
      query: `
        SELECT COUNT(*) AS total_count
        FROM (
            SELECT
                pae.power_asset_id,
                ps.player_id,
                pae.occurred_datetime
            FROM
                power_asset_event pae
            JOIN
                photo_of_power_asset ppa ON ppa.power_asset_id = pae.power_asset_id
                AND ppa.player_id = pae.player_id
                AND ppa.game_space_id = pae.game_space_id
            JOIN
                power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
            JOIN
                game_space gs ON gs.id = pae.game_space_id
            JOIN
                game_term gt ON gt.id = gs.game_term_id
            JOIN
                player_state ps ON pae.player_id = ps.player_id
            JOIN
                ${assetDetailQuery}
            JOIN
            review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
        JOIN
            review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
        JOIN
            review_request_info rri ON rri.id = rririb.review_request_info_id
        JOIN
            review_request_review_request_info_bind rrpb ON rrpb.review_request_info_id = rri.id
            JOIN
                power_asset pa ON pa.id = pae.power_asset_id
            INNER JOIN review_request_event rre ON rrpb.review_request_id = rre.review_request_id
            ${bogokanJoinQuery}
            ${plantInsulateSwitchJoinQuery}
            WHERE
                pae.type = 'PHOTOGRAPH'
                ${reviewRequestState}
                ${companyQuery}
                ${dateRangeQuery}
                ${seasonIdQuery}
                ${missionIdQUery}
                ${assetIdQuery}
                ${signNumberQuery}
                ${assetTypeQuery}
                ${assetTypeQuery2}
                ${bogokanWhereQuery}
                ${managementGroupQuery}
                ${plantInsulateSwitchWhereQuery}
            GROUP BY
                pae.power_asset_id, ps.player_id, occurred_datetime
        ) AS subquery;
      `,
    };
    if (isNewQuery) {
      sqlQueryCount.query = `
        WITH ReviewFilteredPhotos AS (
          SELECT DISTINCT
            ppa.id AS ppa_id,
            ppa.power_asset_id
          FROM power_asset_event pae
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id = pae.player_id
            AND ppa.game_space_id = pae.game_space_id
          WHERE pae.type = 'PHOTOGRAPH'
            AND pae.game_space_id = '${type === "season" ? seasonIdSelected : missioIdSelected}'
            ${dateRangeQuery}
            ${seasonIdQuery}
            ${missionIdQUery}
            AND ${photoReviewStatusQuery}
        ),
        RankedPhotos AS (
          SELECT
            pae.power_asset_id,
            pae.player_id,
            pae.occurred_datetime,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime ${orderValue}
            ) AS rn
          FROM ReviewFilteredPhotos rfp
          JOIN power_asset_event pae
            ON pae.power_asset_id = rfp.power_asset_id
          AND pae.type = 'PHOTOGRAPH'
          AND pae.game_space_id = '${type === "season" ? seasonIdSelected : missioIdSelected}'
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id = pae.player_id
          AND ppa.game_space_id = pae.game_space_id
          JOIN ${assetDetailQuery}
          JOIN power_asset pa ON pa.id = pae.power_asset_id
          ${bogokanJoinQuery}
          ${plantInsulateSwitchJoinQuery}
          WHERE 1=1
            ${companyQuery}
            ${dateRangeQuery}
            ${seasonIdQuery}
            ${missionIdQUery}
            ${assetIdQuery}
            ${signNumberQuery}
            ${assetTypeQuery}
            ${assetTypeQuery2}
            ${bogokanWhereQuery}
            ${managementGroupQuery}
            ${plantInsulateSwitchWhereQuery}
        )
        SELECT COUNT(*) AS total_count
        FROM RankedPhotos
        WHERE rn = 1;
      `;
    }

    let top10FileData, countFileData;
    if (!isNtt) {
      const [imageFileDataTemp, imageFileDataCountTemp] = yield all([
        call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API_REPLICA),
        call(getDataFromKpiDash, sqlQueryCount, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      top10FileData = JSON.parse(imageFileDataTemp.body);
      countFileData = JSON.parse(imageFileDataCountTemp.body);
    } else {
      top10FileData = JSON.parse(nttKitaKantoTop10.body);
      countFileData = JSON.parse(nttKitaKantoTotalCount.body);
    }

    let photoPathArray = [],
      photoFileNameArray = [];
    _.each(top10FileData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
    });

    // Step 3: Get tags by fileName
    const email = yield call(getLocalStorageItem, "email");
    const table =
      env === "prod" ? "PicTreeViewerTagProd" : "PicTreeViewerTagDev";
    const savedTagCalls = photoFileNameArray.map((fileName, index) => {
      const sqlQueryGetTag = {
        query: `
          SELECT file_name, tag_array, tag_flat FROM ${table} WHERE user_email = '${email}' AND file_name = '${fileName}';
        `,
        type: "select",
      };
      return call(
        getDataFromKpiDash,
        sqlQueryGetTag,
        ENDPOINT.GET_DATA_KPIDASH_API
      );
    });
    const savedTagByFileNameResponses = yield all(savedTagCalls);

    let photoAllData = [];
    photoAllData = top10FileData.map((photoData, index) => ({
      signNumber: isNtt ? photoData.prefectural_area : photoData.sign_number,
      signName: isNtt
        ? photoData.exchange_office_name
        : photoData.sign_name !== null && photoData.sign_name !== undefined
        ? photoData.sign_name
        : "--",
      name: isNtt ? photoData.asset_info : photoData.equipment_number,
      placeCode: isNtt
        ? photoData.photography_target
        : photoData.place_code !== null
        ? photoData.place_code
        : "--",
      tag:
        savedTagByFileNameResponses[index] &&
        savedTagByFileNameResponses[index]?.length > 0
          ? JSON.parse(savedTagByFileNameResponses[index][0][1]["stringValue"])
          : [],
      user: photoData.name,
      fileUrl: `${S3_BUCKET_BASE_URL}/${photoPathArray[index]}`,
      fileName: photoData.file_name.split(".jpg")[0],
      occurred_datetime: photoData.occurred_datetime_jst,
      latitude: photoData.latitude,
      longitude: photoData.longitude,
    }));
    yield put(setFilteredTotal(countFileData[0]["total_count"]));
    yield put(setHomeImageData(photoAllData.length === 0 ? [] : photoAllData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export function* handleGetSavedTagDataByEmail() {
  try {
    yield put(setPageTopLoader(true));
    const env = process.env.REACT_APP_ENV;
    const jwtToken = yield call(getJwtToken);
    const email = yield call(getLocalStorageItem, "email");
    const assetFilter = yield select(filter);
    const tagWhereClause = assetFilter.tag
      .map((tag) => `tag_flat LIKE '%${tag}%'`)
      .join(" OR ");
    const table =
      env === "prod" ? "PicTreeViewerTagProd" : "PicTreeViewerTagDev";
    const sqlQueryGetTag = {
      query: `
          SELECT * FROM ${table} WHERE user_email = '${email}' AND (${tagWhereClause});
        `,
      type: "select",
    };
    const [getTagDataTemp] = yield all([
      call(getDataFromKpiDash, sqlQueryGetTag, ENDPOINT.GET_DATA_KPIDASH_API),
    ]);
    if (getTagDataTemp.length > 0) {
      let photoPathArray = [],
        photoFileNameArray = [];
      _.each(getTagDataTemp, function (photoData) {
        photoPathArray.push(photoData[4]["stringValue"]);
        photoFileNameArray.push(photoData[3]["stringValue"]);
      });

      const signedUrlCalls = photoPathArray.map((path, index) => {
        return call(
          getDataFromSxi,
          {
            filePath: path,
            fileName: photoFileNameArray[index],
          },
          ENDPOINT.GET_DATA_SXI_PHOTO_BY_FILE,
          jwtToken
        );
      });
      const signedUrlResponses = yield all(signedUrlCalls);
      const recent10PhotoSignedUrls = signedUrlResponses.map((response) => {
        return JSON.parse(response.body).signedUrl;
      });

      const photoAllData = getTagDataTemp.map((photoData, index) => ({
        signNumber: photoData[11]["stringValue"],
        name: photoData[12]["stringValue"],
        tag: JSON.parse(photoData[15]["stringValue"]),
        user: photoData[14]["stringValue"],
        fileUrl: recent10PhotoSignedUrls[index],
        fileName: photoData[3]["stringValue"].split(".jpg")[0],
        occurred_datetime: photoData[5]["stringValue"],
      }));
      yield put(setFilteredTotal(getTagDataTemp.length));
      yield put(setHomeImageData(photoAllData));
    } else {
      yield put(setFilteredTotal(0));
      yield put(setHomeImageData([]));
    }
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export function* handleHomeImageDataNtt(action) {
  try {
    const requestSeasonId = yield select(selectedSeason);
    if (isSaitamaSeasonSelected(localStorage.getItem("access_key"), requestSeasonId)) {
      yield* handleHomeImageDataSaitama(action);
      return;
    }
    const isMe = localStorage.getItem("access_key") === "qf70ypdjdzm89ypqvc4r7nf9" ? true : false;
    const jwtToken = yield call(getJwtToken);
    yield put(setPageTopLoader(true));
    yield put(setFilteredTotal("--"));
    const env = process.env.REACT_APP_ENV;
    const type = yield select(periodType);
    let dateFrom = yield select(startDate);
    let dateTo = yield select(endDate);

    const assetFilter = yield select(filterNtt);
    const reviewStatus =
      normalizePhotoReviewStatus(assetFilter.reviewStatus) ||
      PHOTO_REVIEW_STATUS.APPROVED;
    const photoReviewStatusQuery = buildPhotoReviewStatusExistsClause({
      photoAlias: "ppa",
      status: reviewStatus,
    });
    const seasonIdSelected = requestSeasonId;
    const missionIdSelected = yield select(selectedMission);
    const pageNumber =
      action.payload.pageNumber === undefined
        ? 1
        : action.payload.pageNumber || 1;
    const itemsPerPage = 10;
    const offset = (pageNumber - 1) * itemsPerPage;
    const orderValue = assetFilter.order == "desc" ? "DESC" : "ASC";
    const table =
      env === "prod"
        ? "PicTreeViewerResultTagNttProd"
        : "PicTreeViewerResultTagNttDev";

    const gameSpaceIdSelected = type === "season" ? seasonIdSelected : missionIdSelected;

    const seasonIdQuery =
      type === "season"
        ? `AND ppa.game_space_id = '${gameSpaceIdSelected}'`
        : `AND pae.game_space_id = '${gameSpaceIdSelected}'`;

    const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // 半開区間：dateTo の翌日 00:00 JST を UTC にして「未満」で切る
    const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const dateRangeQuery =
      (type === "season" || type === "mission")
        ? `AND pae.occurred_datetime >= '${utcFrom}' AND pae.occurred_datetime <  '${utcToExclusive}'`
        : "";

    const padTypeQueryJoin = gameSpaceIdSelected === "cmb68qvei00011vo0e591229z"
      ? `INNER JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id`
      : ``;
    const nttDetailContext = buildCompanyDetailContext({
      provider: "ntt",
      type,
      isMe,
      gameSpaceIdSelected,
    });
    const padTypeQuery = nttDetailContext.padTypeWhereClause;
    // PH3.0.0以降はNTT一覧を新クエリへ統一適用する。
    let isNewQuery = true;

    let detailColumns = `
      padtep.prefectural_area,
      padtep.exchange_office_name,
      padtep.asset_info,
      padtep.photography_target,
    `;

    if (type === "mission") {
      isNewQuery = true; // NTT ME and FT, all mission uses new query
      detailColumns = `padtep.venue_name,`;
    }
    const companyTableName = nttDetailContext.detailTableName;

    //　県域
    const prefecturalArea =
      assetFilter.prefecturalArea === "all"
        ? false
        : assetFilter.prefecturalArea;
    const prefecturalAreaQuery =
      prefecturalArea && prefecturalArea !== "all"
        ? `AND padtep.prefectural_area LIKE '%${prefecturalArea}%'`
        : "";

    // 収容局名
    const exchangeOfficeName =
      assetFilter.exchangeOfficeName === "all"
        ? false
        : assetFilter.exchangeOfficeName;
    const exchangeOfficeNameQuery =
      exchangeOfficeName && exchangeOfficeName !== "all"
        ? `AND padtep.exchange_office_name LIKE '%${exchangeOfficeName}%'`
        : "";

    // 設備番号 || 	電柱識別番号
    const assetInfo =
      assetFilter.assetInfo === "all"
        ? false
        : assetFilter.assetInfo;
    const assetInfoQuery =
      assetInfo && assetInfo !== "all"
        ? `AND padtep.asset_info LIKE '%${assetInfo}%'`
        : "";

    // 撮影対象
    const photographyTarget =
      assetFilter.photographyTarget === "all"
        ? false
        : assetFilter.photographyTarget;
    const photographyTargetQuery =
      photographyTarget && photographyTarget !== "all"
        ? `AND padtep.photography_target LIKE '%${photographyTarget}%'`
        : "";

    const concretePhotoSubTypes = `'TELEGRAPH_CONCRETE_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_CONCRETE_LUCKY_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_LUCKY_POLE', 'JOINT_USE_POLE', 'JOINT_USE_POLE_HAS_SUPPORT_POLE', 'JOINT_USE_LUCKY_POLE', 'JOINT_USE_POLE_HAS_SUPPORT_LUCKY_POLE'`;
    const concreteLuckyPhotoSubTypes = `'TELEGRAPH_CONCRETE_LUCKY_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_LUCKY_POLE', 'JOINT_USE_LUCKY_POLE', 'JOINT_USE_POLE_HAS_SUPPORT_LUCKY_POLE'`;
    const concreteRegularPhotoSubTypes = `'TELEGRAPH_CONCRETE_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_POLE', 'JOINT_USE_POLE', 'JOINT_USE_POLE_HAS_SUPPORT_POLE'`;
    const steelPhotoSubTypes = `'TELEGRAPH_STEEL_PIPE_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_STEEL_PIPE_LUCKY_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_LUCKY_POLE'`;
    const steelLuckyPhotoSubTypes = `'TELEGRAPH_STEEL_PIPE_LUCKY_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_LUCKY_POLE'`;
    const steelRegularPhotoSubTypes = `'TELEGRAPH_STEEL_PIPE_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_POLE'`;

    // 撮影種別
    let photoType =
      assetFilter.photoType === "all" ? false : assetFilter.photoType;
    let photoTypeQuery = "";
    if (photoType) {
      switch (photoType) {
        case "CONCRETE":
          photoTypeQuery = `AND pa.sub_type IN (${concretePhotoSubTypes})`;
          break;
        case "CONCRETE_LUCKY":
          photoTypeQuery = `AND pa.sub_type IN (${concreteLuckyPhotoSubTypes})`;
          break;
        case "CONCRETE_REGULAR":
          photoTypeQuery = `AND pa.sub_type IN (${concreteRegularPhotoSubTypes})`;
          break;
        case "STEEL":
          photoTypeQuery = `AND pa.sub_type IN (${steelPhotoSubTypes})`;
          break;
        case "STEEL_LUCKY":
          photoTypeQuery = `AND pa.sub_type IN (${steelLuckyPhotoSubTypes})`;
          break;
        case "STEEL_REGULAR":
          photoTypeQuery = `AND pa.sub_type IN (${steelRegularPhotoSubTypes})`;
          break;
        case "TARGET_LUCKY":
          photoTypeQuery = `AND pa.sub_type IN (${concreteLuckyPhotoSubTypes}, ${steelLuckyPhotoSubTypes})`;
          break;
        case "TARGET_REGULAR":
          photoTypeQuery = `AND pa.sub_type IN (${concreteRegularPhotoSubTypes}, ${steelRegularPhotoSubTypes})`;
          break;
        default:
          photoTypeQuery = "";
      }
    }

    let photoTypeKpiQuery =
      photoType && photoType == "CONCRETE"
        ? `AND sub_type IN (${concretePhotoSubTypes})`
        : photoType && photoType == "STEEL"
        ? `AND sub_type IN (${steelPhotoSubTypes})`
        : "";

    //　二段階判定対象
    const targetSubType = `'TELEGRAPH_CONCRETE_LUCKY_POLE',
                             'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_LUCKY_POLE',
                             'JOINT_USE_LUCKY_POLE',
                             'JOINT_USE_POLE_HAS_SUPPORT_LUCKY_POLE',
                             'TELEGRAPH_STEEL_PIPE_LUCKY_POLE',
                             'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_LUCKY_POLE'`;
    photoType =
      assetFilter.twoStepVerification === "all"
        ? false
        : assetFilter.twoStepVerification;
    photoTypeQuery =
      photoType && photoType == "target"
        ? `AND pa.sub_type IN (${targetSubType})`
        : photoType && photoType == "non-target"
        ? `AND pa.sub_type NOT IN (${targetSubType})`
        : photoTypeQuery;

    photoTypeKpiQuery =
      photoType && photoType == "target"
        ? `AND sub_type IN (${targetSubType})`
        : photoType && photoType == "non-target"
        ? `AND sub_type NOT IN (${targetSubType})`
        : photoTypeKpiQuery;

    // ME判定結果 if resultTag is not empty, then fetch data by resultTag from KPI RDS
    let resultTagAddedCount = 0,
      reviewReqIdInClause = ``;
    const resultTag =
      assetFilter.resultTag === "all" ? false : assetFilter.resultTag;
    if (resultTag && resultTag !== "all" && resultTag !== "unchecked") {
      const tagCondition =
        resultTag && resultTag !== "all" ? `tag = '${resultTag}'` : "";
      const sqlQueryGetResultTag = {
        query: `
            SELECT review_request_id
            FROM ${table}
            WHERE ${tagCondition}
            AND game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const sqlQueryGetResultTagCount = {
        query: `
            SELECT COUNT(*) AS total_count
            FROM ${table}
            WHERE ${tagCondition}
            AND game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery};
          `,
        type: "select",
      };
      const [getResultTagDataTemp, getResultTagDataCountTemp] = yield all([
        call(
          getDataFromKpiDash,
          sqlQueryGetResultTag,
          ENDPOINT.GET_DATA_KPIDASH_API
        ),
        call(
          getDataFromKpiDash,
          sqlQueryGetResultTagCount,
          ENDPOINT.GET_DATA_KPIDASH_API
        ),
      ]);
      const reviewReqIds = getResultTagDataTemp.map(
        (r) => `'${r[0].stringValue}'`
      );
      resultTagAddedCount = getResultTagDataCountTemp[0][0]["longValue"];
      reviewReqIdInClause = reviewReqIds.length
        ? `AND rrpb.review_request_id IN (${reviewReqIds.join(",")})`
        : ``;
    if (resultTagAddedCount === 0) {
        if (requestSeasonId !== (yield select(selectedSeason))) {
          yield put(setPageTopLoader(false));
          return;
        }
        yield put(setHomeImageDataHokuden([]));
        yield put(setFilteredTotal(0));
        yield put(setPageTopLoader(false));
        return;
      }
    } else if (resultTag && resultTag === "unchecked") {
      const sqlQueryGetAllResultTag = {
        query: `
            SELECT review_request_id
            FROM ${table}
            WHERE game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const sqlQueryGetAllResultTagCount = {
        query: `
            SELECT COUNT(*) AS total_count
            FROM ${table}
            WHERE game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const [getResultAllTagDataTemp, getResultAllTagDataCountTemp] = yield all(
        [
          call(
            getDataFromKpiDash,
            sqlQueryGetAllResultTag,
            ENDPOINT.GET_DATA_KPIDASH_API
          ),
          call(
            getDataFromKpiDash,
            sqlQueryGetAllResultTagCount,
            ENDPOINT.GET_DATA_KPIDASH_API
          ),
        ]
      );
      const reviewReqIds = getResultAllTagDataTemp.map(
        (r) => `'${r[0].stringValue}'`
      );
      resultTagAddedCount = getResultAllTagDataCountTemp[0][0]["longValue"];
      reviewReqIdInClause = reviewReqIds.length
        ? `AND rrpb.review_request_id NOT IN (${reviewReqIds.join(",")})`
        : ``;
    }

    let sqlQuery = {};
    sqlQuery.query = `
      WITH RankedPhotos AS (
        SELECT
          ppa.id AS ppa_id,
          ppa.power_asset_id,
          ppa.player_id,
          ppa.file_name,
          ppa.object_key,
          gs.id AS game_space_id,
          gt.id AS game_term_id,
          pae.occurred_datetime,
          CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
          ps.name,
          rrpb.review_request_id,
          padtep.prefectural_area,
          padtep.exchange_office_name,
          padtep.asset_info,
          padtep.photography_target,
          pa.latitude,
          pa.longitude,
          ROW_NUMBER() OVER (
            PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
            ORDER BY pae.occurred_datetime ${orderValue}
          ) AS rn
        FROM
          power_asset_event pae
        INNER JOIN photo_of_power_asset ppa
          ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id = pae.player_id
          AND ppa.game_space_id = pae.game_space_id
        ${padTypeQueryJoin}
        INNER JOIN game_space gs ON gs.id = pae.game_space_id
        INNER JOIN game_term gt ON gt.id = gs.game_term_id
        INNER JOIN player_state ps ON pae.player_id = ps.player_id
        INNER JOIN ${companyTableName} padtep ON pae.power_asset_id = padtep.power_asset_id
        INNER JOIN review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
        INNER JOIN review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
        INNER JOIN review_request_info rri ON rri.id = rririb.review_request_info_id
        INNER JOIN review_request_review_request_info_bind rrpb ON rrpb.review_request_info_id = rri.id
        INNER JOIN review_request_event rre ON rrpb.review_request_id = rre.review_request_id
        INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
        WHERE
          pae.type = 'PHOTOGRAPH'
          AND rre.type = 'APPROVED'
          ${padTypeQuery}
          ${dateRangeQuery}
          ${seasonIdQuery}
          ${photoTypeQuery}
          ${prefecturalAreaQuery}
          ${exchangeOfficeNameQuery}
          ${assetInfoQuery}
          ${photographyTargetQuery}
          ${reviewReqIdInClause}
        GROUP BY
          pae.power_asset_id, ps.player_id, pae.occurred_datetime
      )
      SELECT *
      FROM RankedPhotos
      WHERE rn = 1
      ORDER BY occurred_datetime ${orderValue}
      LIMIT ${itemsPerPage} OFFSET ${offset};
      `;

    if (isNewQuery) {
      sqlQuery.query = `
        WITH FilteredEvents AS (
          SELECT
            pae.power_asset_id,
            pae.player_id,
            pae.game_space_id,
            pae.occurred_datetime
          FROM power_asset_event pae
          WHERE
            pae.type = 'PHOTOGRAPH'
            AND pae.game_space_id = '${gameSpaceIdSelected}'
            ${dateRangeQuery}
        ),
        RankedPhotos AS (
          SELECT
            ppa.id AS ppa_id,
            ppa.power_asset_id,
            ppa.player_id,
            ppa.file_name,
            ppa.object_key,
            gs.id AS game_space_id,
            gt.id AS game_term_id,
            fe.occurred_datetime,
            CONVERT_TZ(fe.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.name,
            rr.id AS review_request_id,
            ${detailColumns}
            pa.latitude,
            pa.longitude,
            ROW_NUMBER() OVER (
              PARTITION BY fe.power_asset_id, fe.player_id, fe.occurred_datetime
              ORDER BY fe.occurred_datetime ${orderValue}
            ) AS rn
          FROM FilteredEvents fe
          INNER JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = fe.power_asset_id
          AND ppa.player_id      = fe.player_id
          AND ppa.game_space_id  = fe.game_space_id
          INNER JOIN power_asset_detail pad
            ON fe.power_asset_id = pad.power_asset_id
          INNER JOIN game_space gs
            ON gs.id = fe.game_space_id
          INNER JOIN game_term gt
            ON gt.id = gs.game_term_id
          INNER JOIN player_state ps
            ON fe.player_id = ps.player_id
          INNER JOIN ${companyTableName} padtep
            ON fe.power_asset_id = padtep.power_asset_id
          INNER JOIN power_asset pa
            ON pa.id = fe.power_asset_id
          INNER JOIN review_request rr
            ON rr.power_asset_id = pa.id
          WHERE
            ${photoReviewStatusQuery}
            ${padTypeQuery}
            ${photoTypeQuery}
            ${prefecturalAreaQuery}
            ${exchangeOfficeNameQuery}
            ${assetInfoQuery}
            ${photographyTargetQuery}
        )
        SELECT *
        FROM RankedPhotos
        WHERE rn = 1
        ORDER BY occurred_datetime ${orderValue}
        LIMIT ${itemsPerPage} OFFSET ${offset};
      `;
    }

    // COUNT
    let sqlQueryCount = {};
    sqlQueryCount.query = `
        SELECT COUNT(*) AS total_count
        FROM (
            SELECT 1
            FROM
                power_asset_event pae
            JOIN
                photo_of_power_asset ppa ON ppa.power_asset_id = pae.power_asset_id
                AND ppa.player_id = pae.player_id
                AND ppa.game_space_id = pae.game_space_id
            ${padTypeQueryJoin}
            JOIN
                player_state ps ON pae.player_id = ps.player_id
            JOIN
                ${companyTableName} padtep ON pae.power_asset_id = padtep.power_asset_id
            JOIN
            review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
        JOIN
            review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
        JOIN
            review_request_info rri ON rri.id = rririb.review_request_info_id
        JOIN
            review_request_review_request_info_bind rrpb ON rrpb.review_request_info_id = rri.id
            JOIN
                power_asset pa ON pa.id = pae.power_asset_id
            JOIN review_request_event rre ON rrpb.review_request_id = rre.review_request_id
            WHERE
                pae.type = 'PHOTOGRAPH'
                AND rre.type = 'APPROVED'
                ${padTypeQuery}
                ${dateRangeQuery}
                ${seasonIdQuery}
                ${photoTypeQuery}
                ${prefecturalAreaQuery}
                ${assetInfoQuery}
                ${photographyTargetQuery}
                ${reviewReqIdInClause}
            GROUP BY
                pae.power_asset_id, ps.player_id, pae.occurred_datetime
        ) AS sub;
      `;
    if (isNewQuery) {
      sqlQueryCount.query = `
        WITH FilteredEvents AS (
          SELECT
            pae.power_asset_id,
            pae.player_id,
            pae.game_space_id,
            pae.occurred_datetime
          FROM power_asset_event pae
          WHERE
            pae.type = 'PHOTOGRAPH'
            AND pae.game_space_id = '${gameSpaceIdSelected}'
            ${dateRangeQuery}
        ),
        RankedPhotos AS (
          SELECT
            ppa.id AS ppa_id,
            ppa.power_asset_id,
            ppa.player_id,
            ppa.file_name,
            ppa.object_key,
            gs.id AS game_space_id,
            gt.id AS game_term_id,
            fe.occurred_datetime,
            CONVERT_TZ(fe.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.name,
            rr.id AS review_request_id,
            ${detailColumns}
            pa.latitude,
            pa.longitude,
            ROW_NUMBER() OVER (
              PARTITION BY fe.power_asset_id, fe.player_id, fe.occurred_datetime
              ORDER BY fe.occurred_datetime ${orderValue}
            ) AS rn
          FROM FilteredEvents fe
          INNER JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = fe.power_asset_id
          AND ppa.player_id      = fe.player_id
          AND ppa.game_space_id  = fe.game_space_id
          INNER JOIN power_asset_detail pad
            ON fe.power_asset_id = pad.power_asset_id
          INNER JOIN game_space gs
            ON gs.id = fe.game_space_id
          INNER JOIN game_term gt
            ON gt.id = gs.game_term_id
          INNER JOIN player_state ps
            ON fe.player_id = ps.player_id
          INNER JOIN ${companyTableName} padtep
            ON fe.power_asset_id = padtep.power_asset_id
          INNER JOIN power_asset pa
            ON pa.id = fe.power_asset_id
          INNER JOIN review_request rr
            ON rr.power_asset_id = pa.id
          WHERE
            ${photoReviewStatusQuery}
            ${padTypeQuery}
            ${photoTypeQuery}
            ${prefecturalAreaQuery}
            ${exchangeOfficeNameQuery}
            ${assetInfoQuery}
            ${photographyTargetQuery}
          GROUP BY fe.power_asset_id, ps.player_id, fe.occurred_datetime
        )
        SELECT COUNT(*) AS total_count
        FROM RankedPhotos
        WHERE rn = 1;
      `;
    }

    let top10FileData, countFileData;
    const [imageFileDataTemp, imageFileDataCountTemp] = yield all([
      call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API_REPLICA),
      call(getDataFromKpiDash, sqlQueryCount, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    top10FileData = JSON.parse(imageFileDataTemp.body);
    countFileData = JSON.parse(imageFileDataCountTemp.body);

    let photoPathArray = [],
      photoFileNameArray = [],
      reviewReqArray = [];
    _.each(top10FileData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
      reviewReqArray.push(photoData.review_request_id);
    });

    const savedTagCalls = reviewReqArray.map((reviewReqId, index) => {
      const sqlQueryGetTag = {
        query: `
            SELECT review_request_id, tag, reject_reason FROM ${table} WHERE review_request_id = '${reviewReqId}';
          `,
        type: "select",
      };
      return call(
        getDataFromKpiDash,
        sqlQueryGetTag,
        ENDPOINT.GET_DATA_KPIDASH_API
      );
    });
    const savedTagByFileNameResponses = yield all(savedTagCalls);

    let photoAllData = [];
    photoAllData = top10FileData.map((photoData, index) => {
      const baseName = photoData.file_name.replace(/\.(jpg|webp)$/i, "");
      return {
        signNumber: photoData.prefectural_area,
        signName: photoData.exchange_office_name,
        name: photoData.asset_info,
        placeCode: photoData.photography_target,
        tag:
          savedTagByFileNameResponses[index] &&
          savedTagByFileNameResponses[index]?.length > 0
            ? savedTagByFileNameResponses[index][0][1]["stringValue"] === "true"
              ? "合格"
              : savedTagByFileNameResponses[index][0][1]["stringValue"] ===
                "false"
              ? "不合格"
              : savedTagByFileNameResponses[index][0][1]["stringValue"] ===
                "pending"
              ? "保留"
              : savedTagByFileNameResponses[index][0][1]["stringValue"]
            : null,
        user: photoData.name,
        fileUrl: `${S3_BUCKET_BASE_URL}/${encodeURIComponent(
          photoPathArray[index]
        )}`,
        fileName: encodeURIComponent(photoData.file_name.split(".jpg")[0]),
        occurred_datetime: photoData.occurred_datetime_jst,
        latitude: photoData.latitude,
        longitude: photoData.longitude,
      };
    });

    const totalCountToDisplay = countFileData[0]["total_count"];
    if (requestSeasonId !== (yield select(selectedSeason))) {
      yield put(setPageTopLoader(false));
      return;
    }
    yield put(setFilteredTotal(totalCountToDisplay));
    yield put(
      setHomeImageDataNtt(photoAllData.length === 0 ? [] : photoAllData)
    );
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export function* handleHomeImageDataHokuden(action) {
  try {
    const jwtToken = yield call(getJwtToken);
    yield put(setPageTopLoader(true));
    yield put(setFilteredTotal("--"));
    const env = process.env.REACT_APP_ENV;
    const type = yield select(periodType);
    let dateFrom = yield select(startDate);
    let dateTo = yield select(endDate);
    dateFrom = moment(dateFrom).tz("Asia/Tokyo").format("YYYY-MM-DD");
    dateTo = moment(dateTo).tz("Asia/Tokyo").format("YYYY-MM-DD");

    // JST 00:00:00 → UTC
    const startUtc = moment.tz(`${dateFrom} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    // JST 翌日 00:00:00 → UTC（半開区間）
    const endUtc   = moment.tz(moment(`${dateTo} 00:00:00`).add(1, "day"), "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");

    const assetFilter = yield select(filterHokuden);
    const reviewStatus =
      normalizePhotoReviewStatus(assetFilter.reviewStatus) ||
      PHOTO_REVIEW_STATUS.APPROVED;
    const photoReviewStatusQuery = buildPhotoReviewStatusExistsClause({
      photoAlias: "ppa",
      status: reviewStatus,
    });
    const seasonIdSelected = yield select(selectedSeason);
    const missioIdSelected = yield select(selectedMission);
    const gameSpaceIdSelected =
      type === "season" ? seasonIdSelected : missioIdSelected;
    const isMissionSelected =
      type === "mission" && missioIdSelected !== "blank" && missioIdSelected !== "all";
    const pageNumber =
      action.payload.pageNumber === undefined
        ? 1
        : action.payload.pageNumber || 1;
    const itemsPerPage = 10;
    const offset = (pageNumber - 1) * itemsPerPage;
    const orderValue = assetFilter.order == "desc" ? "DESC" : "ASC";
    const table =
      env === "prod"
        ? "PicTreeViewerResultTagNttProd"
        : "PicTreeViewerResultTagNttDev";
    const seasonIdQuery =
      type === "season" ? `AND ppa.game_space_id = '${seasonIdSelected}'` : "";
    const missionIdQuery =
      type === "mission" && missioIdSelected !== "blank" && missioIdSelected !== "all"
        ? `AND pae.game_space_id = '${missioIdSelected}'`
        : "";

    if (isMissionSelected) {
      const filteredPaeCte = buildFilteredPaeCte({
        startUtc: startUtc,
        endUtc: endUtc,
      }).trim().replace(/^WITH\s+/i, "");
      const missionQuery = `
        WITH ${filteredPaeCte},
        RankedPhotos AS (
          SELECT
            ppa.id AS ppa_id,
            ppa.power_asset_id,
            ppa.player_id,
            ppa.file_name,
            ppa.object_key,
            gs.id AS game_space_id,
            gt.id AS game_term_id,
            pae.occurred_datetime,
            CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.name,
            mn.name AS mission_name,
            pa.latitude,
            pa.longitude,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime ${orderValue}
            ) AS rn
          FROM filtered_pae pae
          JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id = pae.player_id
            AND ppa.game_space_id = pae.game_space_id
          JOIN game_space gs
            ON gs.id = pae.game_space_id
          JOIN mission m
            ON m.id = gs.id
          JOIN mission_name mn
            ON mn.mission_id = m.id
            AND mn.language_code = 'JA'
          JOIN game_term gt
            ON gt.id = gs.game_term_id
          JOIN player_state ps
            ON pae.player_id = ps.player_id
          JOIN power_asset pa
            ON pa.id = pae.power_asset_id
          WHERE
            gs.type = 'MISSION'
            AND pa.type = 'MISSION_ASSET'
            AND ${photoReviewStatusQuery}
            AND pae.game_space_id = '${missioIdSelected}'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime < '${endUtc}'
          GROUP BY
            pae.power_asset_id, ps.player_id, pae.occurred_datetime
        )
        SELECT *
        FROM RankedPhotos
        WHERE rn = 1
        ORDER BY occurred_datetime ${orderValue}
        LIMIT ${itemsPerPage} OFFSET ${offset};
      `;

      const missionQueryCount = `
        WITH ${filteredPaeCte}
        SELECT COUNT(DISTINCT pae.power_asset_id, pae.player_id, pae.occurred_datetime) AS total_count
        FROM filtered_pae pae
        JOIN photo_of_power_asset ppa
          ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id = pae.player_id
          AND ppa.game_space_id = pae.game_space_id
        JOIN game_space gs
          ON gs.id = pae.game_space_id
        JOIN mission m
          ON m.id = gs.id
        JOIN mission_name mn
          ON mn.mission_id = m.id
          AND mn.language_code = 'JA'
        JOIN game_term gt
          ON gt.id = gs.game_term_id
        JOIN player_state ps
          ON pae.player_id = ps.player_id
        JOIN power_asset pa
          ON pa.id = pae.power_asset_id
        WHERE
          gs.type = 'MISSION'
          AND pa.type = 'MISSION_ASSET'
          AND ${photoReviewStatusQuery}
          AND pae.game_space_id = '${missioIdSelected}'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime < '${endUtc}';
      `;

      const [imageFileDataTemp, imageFileDataCountTemp] = yield all([
        call(getDataFromKpiDash, { query: missionQuery, type: "select" }, ENDPOINT.GET_DATA_API_REPLICA),
        call(getDataFromKpiDash, { query: missionQueryCount, type: "select" }, ENDPOINT.GET_DATA_API_REPLICA),
      ]);
      const top10FileData = JSON.parse(imageFileDataTemp.body);
      const countFileData = JSON.parse(imageFileDataCountTemp.body);

      let photoPathArray = [],
        photoFileNameArray = [];
      _.each(top10FileData, function (photoData) {
        photoPathArray.push(photoData.object_key);
        photoFileNameArray.push(photoData.file_name);
      });

      let photoAllData = [];
      photoAllData = top10FileData.map((photoData, index) => ({
        signNumber: "",
        signName: "",
        name: "",
        placeCode: "",
        tag: [],
        user: photoData.name,
        fileUrl: `${S3_BUCKET_BASE_URL}/${encodeURIComponent(
          photoPathArray[index]
        )}`,
        fileName: photoData.file_name.replace(/\.(jpg|webp)$/i, ""),
        occurred_datetime: photoData.occurred_datetime_jst,
        latitude: photoData.latitude,
        longitude: photoData.longitude,
      }));

      yield put(setFilteredTotal(countFileData[0]?.total_count ?? 0));
      yield put(setHomeImageDataHokuden(photoAllData.length === 0 ? [] : photoAllData));
      yield put(setPageTopLoader(false));
      return;
    }

    //　電柱番号
    const powerAssetNumber =
      assetFilter.powerAssetNumber === "all"
        ? false
        : assetFilter.powerAssetNumber;
    const powerAssetNumberQuery =
      powerAssetNumber && powerAssetNumber !== "all"
        ? `AND padtep.power_pole_number LIKE '%${powerAssetNumber}%'`
        : "";

    //　撮影者
    const username =
      assetFilter.username === "all"
        ? false
        : assetFilter.username;
    const usernameQuery =
      username && username !== "all"
        ? `AND ps.name LIKE '%${username}%'`
        : "";

    // 市町村名
    const cityName =
      assetFilter.municipality === "all" ? false : assetFilter.municipality;
    const cityNameQuery =
      cityName && cityName !== "all"
        ? `AND padtep.representative_municipality_code = '${cityName}'`
        : "";

    // 支店コード
    const branchName =
      assetFilter.branchName === "all" ? false : assetFilter.branchName;
    const branchNameQuery =
      branchName && branchName !== "all"
        ? `AND padtep.branch_code = '${branchName}'`
        : "";

    let photoType =
      assetFilter.photoType === "all" ? false : assetFilter.photoType;
    let photoTypeQuery =
      photoType && photoType == "CONCRETE"
        ? `AND pa.sub_type IN ('TELEGRAPH_CONCRETE_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_CONCRETE_LUCKY_POLE')`
        : photoType && photoType == "STEEL"
        ? `AND pa.sub_type IN ('TELEGRAPH_STEEL_PIPE_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_STEEL_PIPE_LUCKY_POLE')`
        : "";

    let photoTypeKpiQuery =
      photoType && photoType == "CONCRETE"
        ? `AND sub_type IN ('TELEGRAPH_CONCRETE_POLE', 'TELEGRAPH_CONCRETE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_CONCRETE_LUCKY_POLE')`
        : photoType && photoType == "STEEL"
        ? `AND sub_type IN ('TELEGRAPH_STEEL_PIPE_POLE', 'TELEGRAPH_STEEL_PIPE_POLE_HAS_SUPPORT_POLE', 'TELEGRAPH_STEEL_PIPE_LUCKY_POLE')`
        : "";

    // ME判定結果 if resultTag is not empty, then fetch data by resultTag from KPI RDS
    let resultTagAddedCount = 0,
      reviewReqIdInClause = ``;
    const resultTag =
      assetFilter.resultTag === "all" ? false : assetFilter.resultTag;
    if (resultTag && resultTag !== "all" && resultTag !== "unchecked") {
      const tagCondition =
        resultTag && resultTag !== "all" ? `tag = '${resultTag}'` : "";
      const sqlQueryGetResultTag = {
            query: `
            SELECT review_request_id
            FROM ${table}
            WHERE ${tagCondition}
            AND game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const sqlQueryGetResultTagCount = {
            query: `
            SELECT COUNT(*) AS total_count
            FROM ${table}
            WHERE ${tagCondition}
            AND game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery};
          `,
        type: "select",
      };
      const [getResultTagDataTemp, getResultTagDataCountTemp] = yield all([
        call(
          getDataFromKpiDash,
          sqlQueryGetResultTag,
          ENDPOINT.GET_DATA_KPIDASH_API
        ),
        call(
          getDataFromKpiDash,
          sqlQueryGetResultTagCount,
          ENDPOINT.GET_DATA_KPIDASH_API
        ),
      ]);
      const reviewReqIds = getResultTagDataTemp.map(
        (r) => `'${r[0].stringValue}'`
      );
      resultTagAddedCount = getResultTagDataCountTemp[0][0]["longValue"];
      reviewReqIdInClause = reviewReqIds.length
        ? `AND rrpb.review_request_id IN (${reviewReqIds.join(",")})`
        : ``;
      if (resultTagAddedCount === 0) {
        yield put(setHomeImageDataNtt([]));
        yield put(setFilteredTotal(0));
        yield put(setPageTopLoader(false));
        return;
      }
    } else if (resultTag && resultTag === "unchecked") {
      const sqlQueryGetAllResultTag = {
            query: `
            SELECT review_request_id
            FROM ${table}
            WHERE game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const sqlQueryGetAllResultTagCount = {
            query: `
            SELECT COUNT(*) AS total_count
            FROM ${table}
            WHERE game_space_id = '${gameSpaceIdSelected}'
            ${photoTypeKpiQuery}
          `,
        type: "select",
      };
      const [getResultAllTagDataTemp, getResultAllTagDataCountTemp] = yield all(
        [
          call(
            getDataFromKpiDash,
            sqlQueryGetAllResultTag,
            ENDPOINT.GET_DATA_KPIDASH_API
          ),
          call(
            getDataFromKpiDash,
            sqlQueryGetAllResultTagCount,
            ENDPOINT.GET_DATA_KPIDASH_API
          ),
        ]
      );
      const reviewReqIds = getResultAllTagDataTemp.map(
        (r) => `'${r[0].stringValue}'`
      );
      resultTagAddedCount = getResultAllTagDataCountTemp[0][0]["longValue"];
      reviewReqIdInClause = reviewReqIds.length
        ? `AND rrpb.review_request_id NOT IN (${reviewReqIds.join(",")})`
        : ``;
    }

    const hepcoDetailContext = buildCompanyDetailContext({
      provider: "hepco",
    });
    const hepcoDetailRef = hepcoDetailContext.detailTableRef;
    const hepcoPadTypeQuery = hepcoDetailContext.padTypeWhereClause;
    const photoReviewStatusWithRequestFilter = buildPhotoReviewStatusExistsClause({
      photoAlias: "ppa",
      status: reviewStatus,
      reviewReqIdInClause,
    });

    // query 1 : First 10 images
    const sqlQuery = {
      query: `
        WITH RankedPhotos AS (
          SELECT
            ppa.id AS ppa_id,
            ppa.power_asset_id,
            ppa.player_id,
            ppa.file_name,
            ppa.object_key,
            gs.id AS game_space_id,
            gt.id AS game_term_id,
            pae.occurred_datetime,
            CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
            ps.name,
            rrpb.review_request_id,
            padtep.power_pole_number,
            padtep.branch_code,
            padtep.representative_municipality_code,
            padtep.representative_municipality_name,
            pa.latitude,
            pa.longitude,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime ${orderValue}
            ) AS rn
          FROM
            power_asset_event pae
          INNER JOIN photo_of_power_asset ppa
            ON ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id = pae.player_id
            AND ppa.game_space_id = pae.game_space_id
          INNER JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
          INNER JOIN game_space gs ON gs.id = pae.game_space_id
          INNER JOIN game_term gt ON gt.id = gs.game_term_id
          INNER JOIN player_state ps ON pae.player_id = ps.player_id
          INNER JOIN ${hepcoDetailRef}
          INNER JOIN review_image_info rii ON rii.photo_of_power_asset_id = ppa.id
          INNER JOIN review_request_info_review_image_bind rririb ON rririb.review_image_info_id = rii.id
          INNER JOIN review_request_info rri ON rri.id = rririb.review_request_info_id
          INNER JOIN review_request_review_request_info_bind rrpb ON rrpb.review_request_info_id = rri.id
          INNER JOIN power_asset pa ON pa.id = pae.power_asset_id
          WHERE
            pae.type = 'PHOTOGRAPH'
            ${hepcoPadTypeQuery}
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
            ${seasonIdQuery}
            ${missionIdQuery}
            ${photoTypeQuery}
            ${powerAssetNumberQuery}
            ${cityNameQuery}
            ${branchNameQuery}
            ${usernameQuery}
            AND ${photoReviewStatusWithRequestFilter}
          GROUP BY
            pae.power_asset_id, ps.player_id, pae.occurred_datetime
        )
        SELECT *
        FROM RankedPhotos
        WHERE rn = 1
        ORDER BY occurred_datetime ${orderValue}
        LIMIT ${itemsPerPage} OFFSET ${offset};
      `,
      type: "select",
    };

    // query 2 : COUNT
    const sqlQueryCount = {
      query: `
        SELECT COUNT(DISTINCT pae.power_asset_id, pae.player_id, pae.occurred_datetime) AS total_count
        FROM power_asset_event pae
        JOIN photo_of_power_asset ppa
          ON  ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id      = pae.player_id
          AND ppa.game_space_id  = pae.game_space_id
        JOIN power_asset_detail pad
          ON  pad.power_asset_id = pae.power_asset_id ${hepcoPadTypeQuery}
        JOIN ${hepcoDetailRef}
        JOIN player_state ps
          ON  ps.player_id = pae.player_id
        JOIN power_asset pa
          ON  pa.id = pae.power_asset_id
        WHERE
          pae.type = 'PHOTOGRAPH'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime <  '${endUtc}'
          ${seasonIdQuery}
          ${missionIdQuery}
          ${photoTypeQuery}
          ${powerAssetNumberQuery}
          ${cityNameQuery}
          ${branchNameQuery}
          ${usernameQuery}
          AND ${photoReviewStatusWithRequestFilter};
      `,
      type: "select",
    };
    const sqlQueryGetCityNames = {
      query: `
        WITH Base AS (
          SELECT
            padtep.representative_municipality_code AS city_code,
            padtep.representative_municipality_name AS city_name,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime DESC
            ) AS rn
          FROM power_asset_event pae
          JOIN photo_of_power_asset ppa
            ON  ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id      = pae.player_id
            AND ppa.game_space_id  = pae.game_space_id
          JOIN power_asset_detail pad
            ON  pad.power_asset_id = pae.power_asset_id ${hepcoPadTypeQuery}
          JOIN ${hepcoDetailRef}
          WHERE
            pae.type = 'PHOTOGRAPH'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
            ${seasonIdQuery}
            ${missionIdQuery}
            ${photoTypeQuery}
            AND ${photoReviewStatusWithRequestFilter}
        )
        SELECT city_code, city_name, COUNT(*) AS total_count
        FROM Base
        WHERE rn = 1
        GROUP BY city_code, city_name
        ORDER BY total_count DESC;
      `,
    };

    // query 4 : getting branch code
    const sqlQueryGetBranchCode = {
      query: `
        WITH Base AS (
          SELECT
            padtep.branch_code AS branch_code,
            ROW_NUMBER() OVER (
              PARTITION BY pae.power_asset_id, pae.player_id, pae.occurred_datetime
              ORDER BY pae.occurred_datetime DESC
            ) AS rn
          FROM power_asset_event pae
          JOIN photo_of_power_asset ppa
            ON  ppa.power_asset_id = pae.power_asset_id
            AND ppa.player_id      = pae.player_id
            AND ppa.game_space_id  = pae.game_space_id
          JOIN power_asset_detail pad
            ON  pad.power_asset_id = pae.power_asset_id ${hepcoPadTypeQuery}
          JOIN ${hepcoDetailRef}
          WHERE
            pae.type = 'PHOTOGRAPH'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
            ${seasonIdQuery}
            ${missionIdQuery}
            ${photoTypeQuery}
            AND ${photoReviewStatusWithRequestFilter}
        )
        SELECT branch_code, COUNT(*) AS total_count
        FROM Base
        WHERE rn = 1
        GROUP BY branch_code
        ORDER BY total_count DESC;
      `,
    };

    let top10FileData, countFileData;
    const [
      imageFileDataTemp,
      imageFileDataCountTemp,
    ] = yield all([
      call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API_REPLICA),
      call(getDataFromKpiDash, sqlQueryCount, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    top10FileData = JSON.parse(imageFileDataTemp.body);
    countFileData = JSON.parse(imageFileDataCountTemp.body);

    let photoPathArray = [],
      photoFileNameArray = [],
      reviewReqArray = [];
    _.each(top10FileData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
      reviewReqArray.push(photoData.review_request_id);
    });

    const savedTagCalls = reviewReqArray.map((reviewReqId, index) => {
      const sqlQueryGetTag = {
        query: `
            SELECT review_request_id, tag, reject_reason FROM ${table} WHERE review_request_id = '${reviewReqId}';
          `,
        type: "select",
      };
      return call(
        getDataFromKpiDash,
        sqlQueryGetTag,
        ENDPOINT.GET_DATA_KPIDASH_API
      );
    });
    const savedTagByFileNameResponses = yield all(savedTagCalls);

    let photoAllData = [];
    photoAllData = top10FileData.map((photoData, index) => {
      const baseName = photoData.file_name.replace(/\.(jpg|webp)$/i, "");
      return {
        signNumber: photoData.power_pole_number,
        name: photoData.representative_municipality_name,
        placeCode: photoData.branch_code,
        tag:
          savedTagByFileNameResponses[index] &&
          savedTagByFileNameResponses[index]?.length > 0
            ? savedTagByFileNameResponses[index][0][1]["stringValue"] === "true"
              ? "合格"
              : savedTagByFileNameResponses[index][0][1]["stringValue"] ===
                "false"
              ? "不合格"
              : savedTagByFileNameResponses[index][0][1]["stringValue"] ===
                "pending"
              ? "保留"
              : savedTagByFileNameResponses[index][0][1]["stringValue"]
            : null,
        user: photoData.name,
        fileUrl: `${S3_BUCKET_BASE_URL}/${encodeURIComponent(
          photoPathArray[index]
        )}`,
        //fileName: encodeURIComponent(baseName),
        fileName: encodeURIComponent(photoData.file_name.split(".jpg")[0]),
        occurred_datetime: photoData.occurred_datetime_jst,
        latitude: photoData.latitude,
        longitude: photoData.longitude,
      }
    });
    const totalCountToDisplay = countFileData[0]["total_count"];
    yield put(setFilteredTotal(totalCountToDisplay));
    yield put(
      setHomeImageDataHokuden(photoAllData.length === 0 ? [] : photoAllData)
    );
    yield put(setPageTopLoader(false));

    const [
      cityNameDataTemp,
      branchDataTemp,
    ] = yield all([
      call(getDataFromKpiDash, sqlQueryGetCityNames, ENDPOINT.GET_DATA_API_REPLICA),
      call(getDataFromKpiDash, sqlQueryGetBranchCode, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    const cityOptions = [
      { label: "全て", value: "all" },
      ...JSON.parse(cityNameDataTemp.body).map((item) => ({
        label: `${item.city_name} (${item.total_count})`,
        value: item.city_code,
      })),
    ];

    const branchOptions = [
      { label: "全て", value: "all" },
      ...JSON.parse(branchDataTemp.body).map((item) => ({
        label: `${item.branch_code} (${item.total_count})`,
        value: item.branch_code,
      })),
    ];

    yield put(setHokudenOptionCity(cityOptions));
    yield put(setHokudenOptionBranch(branchOptions));
  } catch (error) {
    console.log(error);
  }
}

export function* handleHomeImageDataMission(action) {
  try {
    const jwtToken = yield call(getJwtToken);
    yield put(setPageTopLoader(true));
    yield put(setFilteredTotal("--"));
    const env = process.env.REACT_APP_ENV;
    const type = yield select(periodType);
    let dateFrom = yield select(startDate);
    let dateTo = yield select(endDate);

    const utcFrom = moment.tz(dateFrom, "YYYY-MM-DD", "Asia/Tokyo")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // 半開区間：dateTo の翌日 00:00 JST を UTC にして「未満」で切る
    const utcToExclusive = moment.tz(dateTo, "YYYY-MM-DD", "Asia/Tokyo")
      .add(1, "day")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const assetFilter = yield select(filter);
    const seasonIdSelected = yield select(selectedSeason);
    const missioIdSelected = yield select(selectedMission);
    const missionSelectedName = yield select(selectedMissionLabel);
    dateFrom = moment(dateFrom).tz("Asia/Tokyo").format("YYYY-MM-DD");
    dateTo = moment(dateTo).tz("Asia/Tokyo").format("YYYY-MM-DD");
    const pageNumber =
      action.payload.pageNumber === undefined
        ? 1
        : action.payload.pageNumber || 1;
    const itemsPerPage = 10;
    const offset = (pageNumber - 1) * itemsPerPage;
    const orderValue = assetFilter.order == "desc" ? "DESC" : "ASC";

    const seasonIdQuery =
      type === "season" && seasonIdSelected !== "all"
        ? `AND ppa.game_space_id = '${seasonIdSelected}'`
        : "";

    const missionIdQUery =
      type === "mission" &&
      missioIdSelected !== "blank" &&
      missioIdSelected !== "all"
        ? `AND pae.game_space_id = '${missioIdSelected}'`
        : "";

    const filteredPaeCte = buildFilteredPaeCte({
      startUtc: utcFrom,
      endUtc: utcToExclusive,
    });
    const missionDetailContext = buildCompanyDetailContext({
      provider: "tepco",
      type,
      missionSelectedName,
    });
    const reviewRequestIdSubquery = buildApprovedReviewRequestIdSubquery({
      photoAlias: "ppa",
    });
    const approvedReviewExists = buildApprovedReviewExistsClause({
      photoAlias: "ppa",
    });

    // TEPCO外のミッション対応
    const companyQuery = missionDetailContext.padTypeWhereClause;
    const missionDetailTableRef = missionDetailContext.detailTableRef;

    let sqlQuery = {};
    sqlQuery.query = `
      ${filteredPaeCte}
      SELECT
          ppa.*,
          gs.*,
          gt.*,
          pae.occurred_datetime AS occurred_datetime,
          CONVERT_TZ(pae.occurred_datetime, '+00:00', '+09:00') AS occurred_datetime_jst,
          ps.*,
          pad.*,
          padtep.*,
          pa.latitude,
          pa.longitude,
          ${reviewRequestIdSubquery} AS review_request_id
      FROM
          filtered_pae pae
      JOIN
          photo_of_power_asset ppa ON ppa.power_asset_id = pae.power_asset_id
          AND ppa.player_id = pae.player_id
          AND ppa.game_space_id = pae.game_space_id
      JOIN
          power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
      JOIN
          game_space gs ON gs.id = pae.game_space_id
      JOIN
          game_term gt ON gt.id = gs.game_term_id
      JOIN
          player_state ps ON pae.player_id = ps.player_id
      JOIN
          ${missionDetailTableRef}
      JOIN power_asset pa ON pa.id = pae.power_asset_id
      WHERE
          ${approvedReviewExists}
          ${companyQuery}
          ${seasonIdQuery}
          ${missionIdQUery}
      GROUP BY
          pae.power_asset_id, ps.player_id, pae.occurred_datetime
      ORDER BY
          pae.occurred_datetime ${orderValue}
      LIMIT ${itemsPerPage}
      OFFSET ${offset};
    `;

    // COUNT
    let sqlQueryCount = {
      query: `
        ${filteredPaeCte}
            SELECT COUNT(*) AS total_count
            FROM (
            SELECT
                pae.power_asset_id,
                ps.player_id,
                pae.occurred_datetime
            FROM
                filtered_pae pae
            JOIN
                photo_of_power_asset ppa ON ppa.power_asset_id = pae.power_asset_id
                AND ppa.player_id = pae.player_id
                AND ppa.game_space_id = pae.game_space_id
            JOIN
                power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
            JOIN
                game_space gs ON gs.id = pae.game_space_id
            JOIN
                game_term gt ON gt.id = gs.game_term_id
            JOIN
                player_state ps ON pae.player_id = ps.player_id
                JOIN
                    ${missionDetailTableRef}
            JOIN
                power_asset pa ON pa.id = pae.power_asset_id
            WHERE
                ${approvedReviewExists}
                ${companyQuery}
                ${seasonIdQuery}
                ${missionIdQUery}
            GROUP BY
                pae.power_asset_id, ps.player_id, occurred_datetime
        ) AS subquery;
      `,
    };

    let top10FileData, countFileData;
    const [imageFileDataTemp, imageFileDataCountTemp] = yield all([
      call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API_REPLICA),
      call(getDataFromKpiDash, sqlQueryCount, ENDPOINT.GET_DATA_API_REPLICA),
    ]);
    top10FileData = JSON.parse(imageFileDataTemp.body);
    countFileData = JSON.parse(imageFileDataCountTemp.body);

    let photoPathArray = [],
      photoFileNameArray = [];
    _.each(top10FileData, function (photoData) {
      photoPathArray.push(photoData.object_key);
      photoFileNameArray.push(photoData.file_name);
    });

    // Step 3: Get tags by fileName
    const email = yield call(getLocalStorageItem, "email");
    const table =
      env === "prod" ? "PicTreeViewerTagProd" : "PicTreeViewerTagDev";
    const savedTagCalls = photoFileNameArray.map((fileName, index) => {
      const sqlQueryGetTag = {
        query: `
          SELECT file_name, tag_array, tag_flat FROM ${table} WHERE user_email = '${email}' AND file_name = '${fileName}';
        `,
        type: "select",
      };
      return call(
        getDataFromKpiDash,
        sqlQueryGetTag,
        ENDPOINT.GET_DATA_KPIDASH_API
      );
    });
    const savedTagByFileNameResponses = yield all(savedTagCalls);

    let photoAllData = [];
    photoAllData = top10FileData.map((photoData, index) => ({
      venueName: photoData.venue_name,
      tag:
        savedTagByFileNameResponses[index] &&
        savedTagByFileNameResponses[index]?.length > 0
          ? JSON.parse(savedTagByFileNameResponses[index][0][1]["stringValue"])
          : [],
      user: photoData.name,
      fileUrl: `${S3_BUCKET_BASE_URL}/${photoPathArray[index]}`,
      fileName: photoData.file_name.split(".jpg")[0],
      occurred_datetime: photoData.occurred_datetime_jst,
      latitude: photoData.latitude,
      longitude: photoData.longitude,
    }));
    yield put(setFilteredTotal(countFileData[0]["total_count"]));
    yield put(setHomeMissionImageData(photoAllData.length === 0 ? [] : photoAllData));
    yield put(setPageTopLoader(false));
  } catch (error) {
    console.log(error);
  }
}

export default function* watchMapSettings() {
  yield takeEvery(HOME.REQUEST_SAITAMA_CSV_EXPORT, handleSaitamaCsvExport);
  yield takeEvery(HOME.GET_HOME_IMAGE_DATA, handleHomeImageDataTepco);
  yield takeEvery(HOME.GET_HOME_IMAGE_DATA_NTT, handleHomeImageDataNtt);
  yield takeEvery(HOME.GET_HOME_IMAGE_DATA_HOKUDEN, handleHomeImageDataHokuden);
  yield takeEvery(HOME.GET_HOME_MISSION_IMAGE_DATA, handleHomeImageDataMission);
  yield takeEvery(APP.GET_SAVED_SELECTED_TAG_DATA_BY_EMAIL, handleGetSavedTagDataByEmail);
}
